import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useMatch } from 'react-router-dom';
import { loadAnalytics, refreshAnalytics, loadMarkers, saveMarkers } from '../../services/analyticsService';
import { ExportButton, Section } from './analyticsUi';
import { buildProfitReport } from './exportReport';
import { horizonLabel } from './profit/profitFormat';
import { buildView } from './profit/profitModel';
import Verdict from './profit/Verdict';
import AdPlanning from './profit/AdPlanning';
import Conversion from './profit/Conversion';
import SpendVsInstalls from './profit/SpendVsInstalls';
import { Accuracy, DataHealth, SetupChecklist } from './profit/DataQuality';
import { Campaigns, EarningsPerUser, LastThirtyDays, MoneyPerMonth, SignupMonths } from './profit/EarningsSections';
import '../../styles/analytics.css';

function ProfitPanel() {
  const [data, setData] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [rangeDays, setRangeDays] = useState(90);
  const [openTables, setOpenTables] = useState({ cohorts: true });
  const onAdsPage = !!useMatch('/admin/profit/ads');
  const onConversionPage = !!useMatch('/admin/profit/conversion');

  useEffect(() => {
    Promise.all([loadAnalytics(), loadMarkers().catch(() => [])])
      .then(([analytics, m]) => {
        setData(analytics);
        setMarkers(m);
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleRefresh() {
    setRefreshing(true);
    setError(null);
    try {
      await refreshAnalytics();
      setData(await loadAnalytics());
    } catch (e) {
      console.error('Refresh failed:', e);
      setError(e.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }

  async function addMarker(date, note) {
    setMarkers(await saveMarkers([...markers, { date, note }]));
  }

  async function removeMarker(index) {
    setMarkers(await saveMarkers(markers.filter((_, i) => i !== index)));
  }

  const toggleTable = (key) => setOpenTables(t => ({ ...t, [key]: !t[key] }));

  const view = useMemo(() => buildView(data, rangeDays, markers), [data, rangeDays, markers]);

  if (loading) return <div className="pf-panel pf-panel-wide"><p>Loading analytics...</p></div>;

  const updated = data?.lastUpdated
    ? new Date(data.lastUpdated).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

  const header = (
    <div className="pf-header">
      <div>
        <h1>Profit</h1>
        <p className="pf-hint">Ad spend against what users earn you. Subscriptions are after Google's cut and tax. All in EUR.</p>
      </div>
      <div className="pf-header-actions">
        {updated && <span className="pf-updated">Updated {updated}</span>}
        <ExportButton data={data && { ...data, markers }} name="profit" buildReport={() => buildProfitReport(data, markers)} />
        <button className="btn-secondary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing...' : 'Refresh now'}
        </button>
      </div>
    </div>
  );

  const subTabs = (
    <nav className="subtabs">
      {[{ to: '/admin/profit', label: 'Earnings', end: true }, { to: '/admin/profit/ads', label: 'Ad planning' }, { to: '/admin/profit/conversion', label: 'Conversion' }].map(tab => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) => `subtab-button ${isActive ? 'active' : ''}`}
        >
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );

  const warnings = (error || data?.warnings?.length > 0) && (
    <div className="pf-warnings">
      <strong>Some data is missing, so the numbers below are incomplete.</strong>
      <ul>
        {error && <li>{error}</li>}
        {data?.warnings?.map(w => <li key={w}>{w}</li>)}
      </ul>
    </div>
  );

  if (!view) {
    return (
      <div className="pf-panel pf-panel-wide">
        {header}
        {subTabs}
        {warnings}
        <Section title="Setup" hint="No data yet. Connect the data sources below, then press Refresh now. After that the numbers update every morning at 07:00.">
          <SetupChecklist health={data?.health} />
        </Section>
      </div>
    );
  }

  const { last30, prev30, paidShare, rangeDaily, typical, renewalAdded, returnAge, cohortRows, rangeMarkers, expectedNow, expectedPrev, expectedRange, expectedByMonth, accuracy, forecast, budget } = view;
  const renewal = data.renewal;
  const health = data.health || {};
  const monthly = (data.monthly || []).map(m => ({ ...m, expected: expectedByMonth[m.month] ?? null }));
  // Cash profit with ads as now, for the months ahead (and the running month)
  (forecast?.rows || []).forEach(r => {
    const row = monthly.find(m => m.month === r.month);
    if (row) row.forecast = r.keep;
    else monthly.push({ month: r.month, forecast: r.keep });
  });
  monthly.sort((a, b) => (a.month < b.month ? -1 : 1));
  const horizon = returnAge != null ? horizonLabel(returnAge) : null;
  const costLow = last30.costPerUser;
  const costHigh = costLow && paidShare > 0 && paidShare < 1 ? costLow / paidShare : null;

  return (
    <div className="pf-panel pf-panel-wide">
      {header}
      {subTabs}
      {warnings}

      {onConversionPage ? <Conversion conversion={data.conversion} /> : onAdsPage ? <AdPlanning budget={budget} typical={typical} horizon={horizon} paidShare={paidShare} /> : (
        <>
          <div className="pf-top">
            <Verdict verdict={view.verdict} />
            <LastThirtyDays
              last30={last30} prev30={prev30} expectedNow={expectedNow} expectedPrev={expectedPrev}
              expectedRange={expectedRange} accuracy={accuracy} horizon={horizon}
            />
          </div>

          <div className="pf-columns">
            <MoneyPerMonth monthly={monthly} forecast={forecast} open={openTables.monthly} onToggle={() => toggleTable('monthly')} />
            <EarningsPerUser typical={typical} costLow={costLow} costHigh={costHigh} renewalAdded={renewalAdded} renewal={renewal} />
          </div>

          <SignupMonths cohortRows={cohortRows} returnAge={returnAge} open={openTables.cohorts} onToggle={() => toggleTable('cohorts')} />

          <Campaigns campaigns={data.campaigns} since={data.campaignsSince} />

          <div className="pf-columns">
            <SpendVsInstalls
              rangeDays={rangeDays} onRangeChange={setRangeDays} rangeDaily={rangeDaily} rangeMarkers={rangeMarkers}
              markers={markers} today={view.today} onAddMarker={addMarker} onRemoveMarker={removeMarker}
            />
            <div className="pf-stack">
              <Accuracy accuracy={accuracy} />
              <DataHealth health={health} paidShare={paidShare} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ProfitPanel;
